import { useState, useEffect } from "react";
import { Calculator, Zap, Building2, CreditCard, ArrowRight, Loader2, CheckCircle, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import API from "@/lib/api";
import { toast } from "sonner";

export default function TopUp() {
  const [settings, setSettings] = useState(null);
  const [units, setUnits] = useState("");
  const [payMethod, setPayMethod] = useState("bank_transfer");
  const [payRef, setPayRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gatewaySettings, setGatewaySettings] = useState({ paystack: { enabled: false }, flutterwave: { enabled: false } });

  useEffect(() => {
    Promise.all([
      API.get("/payments/topup-settings"),
      API.get("/payments/gateway-settings/public").catch(() => ({ data: {} })),
    ]).then(([ts, gw]) => {
      setSettings(ts.data);
      if (gw.data) setGatewaySettings(gw.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const unitCount = Math.max(0, parseInt(units) || 0);
  const pricePerCreditKobo = settings ? settings.price_per_100_credits / 100 : 550;
  const feePct = settings ? settings.transaction_fee_percent : 1.5;
  const amountBeforeFeeKobo = unitCount * pricePerCreditKobo;
  const txFeeKobo = (payMethod === "paystack" || payMethod === "flutterwave") ? amountBeforeFeeKobo * (feePct / 100) : 0;
  const totalAmountKobo = amountBeforeFeeKobo + txFeeKobo;

  const fmtN = (n) => `₦${(n / 100).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const submit = async () => {
    if (unitCount < 100) { toast.error("Minimum top-up is 100 units"); return; }
    if (payMethod === "bank_transfer" && !payRef.trim()) { toast.error("Enter your transfer reference"); return; }
    setSubmitting(true);
    try {
      if (payMethod === "bank_transfer") {
        const { data } = await API.post("/payments/topup/bank-transfer", {
          units: unitCount,
          payment_reference: payRef,
        });
        setSuccess(data);
        toast.success("Top-up request submitted!");
      } else if (payMethod === "paystack") {
        const callbackUrl = `${window.location.origin}/payment/callback`;
        const { data } = await API.post("/payments/paystack/initialize-topup", {
          units: unitCount,
          callback_url: callbackUrl,
        });
        window.location.href = data.authorization_url;
      } else if (payMethod === "flutterwave") {
        const redirectUrl = `${window.location.origin}/payment/callback`;
        const { data } = await API.post("/payments/flutterwave/initialize-topup", {
          units: unitCount,
          redirect_url: redirectUrl,
        });
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit top-up");
    } finally { setSubmitting(false); }
  };

  const availableMethods = [
    { id: "bank_transfer", Icon: Building2, label: "Bank Transfer", note: "No fees" },
    ...(gatewaySettings?.paystack?.enabled ? [{ id: "paystack", Icon: CreditCard, label: "Paystack", note: `${feePct}% fee` }] : []),
    ...(gatewaySettings?.flutterwave?.enabled ? [{ id: "flutterwave", Icon: Wallet, label: "Flutterwave", note: `${feePct}% fee` }] : []),
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin text-[#0c1e41]" size={28} />
    </div>
  );

  if (success) return (
    <div className="max-w-md mx-auto mt-16 text-center" data-testid="topup-success">
      <div className="w-16 h-16 bg-emerald-50 border-2 border-emerald-200 rounded-full flex items-center justify-center mx-auto mb-5">
        <CheckCircle size={30} className="text-emerald-600" />
      </div>
      <h2 className="text-xl font-extrabold text-slate-900 mb-2">Top-Up Request Submitted!</h2>
      <p className="text-sm text-slate-500 mb-1">
        <strong>{success.units?.toLocaleString()} credits</strong> will be added once payment is confirmed by admin.
      </p>
      <p className="text-xs text-slate-400 mb-6">Ref: {success.payment_id}</p>
      <div className="flex gap-3 justify-center">
        <Button variant="outline" className="border-slate-300" onClick={() => { setSuccess(null); setUnits(""); setPayRef(""); }}>
          Top-Up Again
        </Button>
        <Button className="bg-[#0c1e41] hover:bg-[#091629]" onClick={() => window.history.back()}>
          Back to Wallet
        </Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-10" data-testid="topup-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Top-Up Credits</h1>
        <p className="text-sm text-slate-500 mt-1">Purchase additional credits on top of your subscription</p>
      </div>

      {/* Calculator card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-[#0c1e41] px-6 py-5 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
            <Calculator size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Credit Calculator</h3>
            <p className="text-xs text-white/60 mt-0.5">{fmtN(pricePerCreditKobo)} per credit · Minimum 100 units</p>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Units input */}
          <div className="space-y-2">
            <Label className="text-slate-700 text-sm font-semibold">Number of Units (Credits)</Label>
            <div className="relative">
              <Input
                type="number"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                placeholder="e.g. 500"
                min={100}
                className="bg-white border-slate-300 text-slate-900 h-12 text-lg font-bold placeholder:text-slate-300 placeholder:font-normal pr-24"
                data-testid="topup-units-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-medium">credits</span>
            </div>
            {unitCount > 0 && unitCount < 100 && (
              <p className="text-xs text-red-500">Minimum top-up is 100 units</p>
            )}
          </div>

          {/* Breakdown table */}
          <div className={`bg-slate-50 border border-slate-200 rounded-xl divide-y divide-slate-200 transition-opacity ${unitCount >= 100 ? "opacity-100" : "opacity-40"}`}>
            <div className="flex items-center justify-between px-5 py-3">
              <span className="text-sm text-slate-600">Unit Price</span>
              <span className="text-sm font-semibold text-slate-900">{fmtN(pricePerCreditKobo)} / credit</span>
            </div>
            <div className="flex items-center justify-between px-5 py-3">
              <span className="text-sm text-slate-600">Amount to Pay</span>
              <span className="text-sm font-semibold text-slate-900">{unitCount >= 100 ? fmtN(amountBeforeFeeKobo) : "—"}</span>
            </div>
            <div className="flex items-center justify-between px-5 py-3">
              <span className="text-sm text-slate-600">
                Transaction Fee {payMethod === "bank_transfer" ? <span className="text-emerald-600 text-xs ml-1">(waived)</span> : <span className="text-slate-400 text-xs ml-1">({feePct}%)</span>}
              </span>
              <span className="text-sm font-semibold text-slate-900">{payMethod === "bank_transfer" ? "₦0.00" : unitCount >= 100 ? fmtN(txFeeKobo) : "—"}</span>
            </div>
            <div className="flex items-center justify-between px-5 py-4 bg-[#eef1f8] rounded-b-xl">
              <span className="text-sm font-bold text-[#0c1e41]">Total Amount</span>
              <span className="text-xl font-black text-[#0c1e41]" data-testid="topup-total">{unitCount >= 100 ? fmtN(totalAmountKobo) : "₦0.00"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment method */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 mb-4">Payment Method</h3>
        <div className="flex gap-2 mb-5">
          {availableMethods.map(({ id, Icon, label, note }) => (
            <button key={id} onClick={() => setPayMethod(id)}
              className={`flex-1 flex flex-col items-center gap-1 py-4 rounded-xl border-2 text-sm font-semibold transition-all ${payMethod === id ? "border-[#0c1e41] bg-[#eef1f8] text-[#0c1e41]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
              data-testid={`topup-method-${id}`}>
              <Icon size={18} />
              {label}
              <span className={`text-[10px] font-normal ${payMethod === id ? "text-[#0c1e41]/60" : "text-slate-400"}`}>{note}</span>
            </button>
          ))}
        </div>

        {payMethod === "bank_transfer" && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-700 leading-relaxed">
              <strong>Transfer {unitCount >= 100 ? fmtN(totalAmountKobo) : "the calculated amount"}</strong> to your configured bank account, then enter your reference below.
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Transfer Reference</Label>
              <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="e.g. your-email@example.com or receipt number"
                className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" data-testid="topup-reference" />
            </div>
          </div>
        )}
        {payMethod === "paystack" && (
          <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-4 text-sm text-center">
            <CreditCard size={24} className="text-[#0c1e41] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 mb-1">Pay with Paystack</p>
            <p className="text-xs text-slate-500">You'll be redirected to Paystack to complete payment securely.</p>
          </div>
        )}
        {payMethod === "flutterwave" && (
          <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-4 text-sm text-center">
            <Wallet size={24} className="text-[#0c1e41] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 mb-1">Pay with Flutterwave</p>
            <p className="text-xs text-slate-500">You'll be redirected to Flutterwave to complete payment securely.</p>
          </div>
        )}

        <Button
          onClick={submit}
          disabled={submitting || unitCount < 100}
          className="w-full mt-5 bg-[#0c1e41] hover:bg-[#091629] h-11 font-bold shadow-lg shadow-[#0c1e41]/15"
          data-testid="topup-submit">
          {submitting
            ? (payMethod === "bank_transfer"
                ? <><Loader2 size={14} className="animate-spin mr-2" />Submitting...</>
                : <><Loader2 size={14} className="animate-spin mr-2" />Redirecting...</>)
            : <>
                {payMethod === "bank_transfer" ? "Request" : "Pay"} {unitCount >= 100 ? `${unitCount.toLocaleString()} Credits` : "Top-Up"}
                <ArrowRight size={14} className="ml-2" />
              </>}
        </Button>
      </div>
    </div>
  );
}
